import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import React from "react";
import { Tenant } from "../../../api/tenantsApi";

interface DeleteTenantDialogProps {
  open: boolean;
  tenant: Tenant | null;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}

const DeleteTenantDialog: React.FC<DeleteTenantDialogProps> = ({
  open,
  tenant,
  onClose,
  onConfirm,
  isLoading,
}) => {
  if (!tenant) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: "1.25rem",
          fontWeight: 600,
          borderBottom: "1px solid",
          borderColor: "divider",
          pb: 2,
        }}
      >
        Delete Tenant
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <DialogContentText sx={{ mb: 2, color: "text.primary" }}>
          Are you sure you want to delete this tenant? This action cannot be undone.
        </DialogContentText>

        {/* Tenant Info */}
        <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Tenant Information
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="body1" fontWeight={600}>
              {tenant.name}
            </Typography>
            <Chip
              label={tenant.isActive ? "Active" : "Inactive"}
              color={tenant.isActive ? "success" : "default"}
              size="small"
            />
          </Box>

          {tenant.contactEmail && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Email: {tenant.contactEmail}
            </Typography>
          )}
          {tenant.contactPhone && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Phone: {tenant.contactPhone}
            </Typography>
          )}
        </Box>

        <DialogContentText
          sx={{
            color: "error.main",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          ⚠️ Warning: This will permanently delete the tenant and all associated data.
        </DialogContentText>
      </DialogContent>
      <DialogActions
        sx={{
          p: 2.5,
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          disabled={isLoading}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          color="error"
          variant="contained"
          disabled={isLoading}
          sx={{
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          {isLoading ? "Deleting..." : "Delete Tenant"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteTenantDialog;

